// İSG uzmanı portalı — Taşeron detayı: Personel sekmesi (api-contract §13.3/§13.5).
// Liste **aktive personel + bekleyen davetleri** birlikte gösterir (`GET
// /contractors/{uuid}/personnel`): ad, e-posta/telefon, **durum rozeti** (Aktif/Pasif/
// Bekliyor/Süresi doldu/İptal), **erişim** (Personeli/Yetkili), davet/aktivasyon tarihi.
//
// Satır aksiyonları **kind'e göre** (§13.3):
//   - Aktive personel (kind = Personnel): **Düzenle** (#29 — kişi çekmecesini DOĞRUDAN düzenleme
//     kipinde açar: ad, T.C., erişim, aktif/pasif → PUT) + **Sil** (bu taşerondan çıkar → DELETE
//     personnel).
//   - Etkinleştirilmemiş kişi (status = NoLogin, aktif): bağlantı yoksa **Aktivasyon bağlantısı
//     gönder**; bekleyen bağlantı varsa **Bağlantıyı tekrar gönder** / **Bağlantıyı iptal et**
//     (iptal kişi satırını TUTAR, yalnız `pendingLink` temizlenir).
//   - Düz bekleyen davet (kind = Invitation): **tekrar gönder** + **iptal**. İptal edilen düz
//     davet listeden KALKAR (Revoked satırlar artık listelenmez, GL6 §4.6).
// Aksiyon sonrası liste invalidate. Yeni personel üstteki **Personel Oluştur** ile
// (`PersonnelCreateSheet`); bağlantı gönder/tekrar gönder `PersonnelLinkModal` ile.
//
// Düzenleme oturumu (`usePersonnelEditor` + `usePersonnelClient`) BU panelde yaşar ve çekmeceye
// geçer: ⋮ "Düzenle" `begin` + çekmeceyi açmayı tek olayda yapar → çekmecenin ilk karesi zaten
// düzenleme kipidir (görünüm karesi, belge isteği yok). Ad düğmesi / gezinme görünüm kipinde açar.
//
// Aktive personelin adı (adsızsa iletişimi) bir düğmedir ve ortak kişi çekmecesini açar
// (`PersonnelDrawer`, GL5); davet satırı düz metin kalır. Kişi başka bir ekrandan (genel arama,
// eski belgeler adresi) router state'iyle de açılabilir (`useConsumeOpenPersonnel`); listede
// olmayan kişi için kapatılabilir bir "bulunamadı" bildirimi gösterilir.
//
// Tablo düzeni ve odak dönüşü Araçlar sekmesiyle (`VehiclesPanel.tsx`) aynıdır (GL #12).
// Satır menüsünden açılan pencereler kapanınca odak o satırın adına, ad düğmesi yoksa
// (davet) satırın ⋮'sine döner. Sil (ve düz davet iptali) satırı kaldırır → odak "Personel Oluştur"e iner
// (`useRowRemovalFocus`). Kişinin bağlantı iptali satırı TUTAR (odak o satırın ⋮'sine döner);
// düz davetin iptali satırı kaldırır (`focus.removed`). İptal hata verirse (ör. bağlantı
// aktive edildi → 404 INVITATION_NOT_FOUND) hata gösterilir, liste tazelenir ve `removeFailed`
// kurulur: satır yenilemede gerçekten kalktıysa odak "Personel Oluştur"a iner.

import {
  Alert,
  Button,
  ConfirmDialog,
  type ContractorPersonnelDto,
  EmptyState,
  PendingLinkLine,
  PersonnelCreateSheet,
  PersonnelLinkModal,
  PersonnelMaskNotice,
  RowActionsMenu,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  Tag,
  contractorsApi,
  currentCulture,
  personLabel,
  resolveApiErrorMessage,
  toEditTarget,
  useConsumeOpenPersonnel,
  usePersonnelEditor,
  usePersonnelLinkActions,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { formatDate } from '../../lib/format';
import { permitsKeys } from '../../lib/permitsKeys';
import { usePersonnelActivationClient } from '../../lib/personnelActivationClient';
import { usePersonnelClient } from '../../lib/personnelClient';
import { TabPanelIntro } from '../TabPanelIntro';
import { PersonnelDrawer } from './PersonnelDrawer';
import { accessLevelTone, personnelStatusLabelKey, personnelStatusTone } from './tone';

const COLUMN_COUNT = 6;

interface PersonnelPanelProps {
  contractorUuid: string;
}

/** İlgili tarih — davette davet anı; etkinleştirilmemiş kişide son bağlantının gönderimi, yoksa aktivasyon. */
function relevantDate(person: ContractorPersonnelDto): string | null {
  if (person.kind === 'Invitation') {
    return person.invitedAt;
  }
  return person.pendingLink?.sentAt ?? person.activatedAt;
}

export function PersonnelPanel({ contractorUuid }: PersonnelPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const culture = currentCulture();

  const [createOpen, setCreateOpen] = useState(false);
  // Oluştur sayfası kapanınca odağın döneceği düğüm (`Sheet` Dialog.Trigger render etmez).
  const createOpenerRef = useRef<HTMLButtonElement | null>(null);
  const [toDelete, setToDelete] = useState<ContractorPersonnelDto | null>(null);
  const activation = usePersonnelActivationClient(contractorUuid);
  // Çekmecesi açık kişi (uuid) ve odağın döneceği düğüm; gezinme isteği listede olmayan kişiyi
  // soruyorsa `notFound` (kapatılabilir bildirim).
  const [drawerUuid, setDrawerUuid] = useState<string | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Satırların ad düğmeleri (yalnız aktive personel) ve ⋮ tetikleyicileri — menüden açılan
  // pencerenin odak dönüş zinciri: ad → ⋮ → "Personel Oluştur".
  const nameButtons = useRef(new Map<string, HTMLButtonElement>());
  const triggers = useRef(new Map<string, HTMLButtonElement>());

  const editor = usePersonnelEditor({
    client: usePersonnelClient(contractorUuid),
    onCloseDrawer: () => setDrawerUuid(null),
  });

  const query = useQuery({
    queryKey: contractorsKeys.personnel(contractorUuid),
    queryFn: () => contractorsApi.listContractorPersonnel(contractorUuid),
  });

  const rows = query.data ?? [];
  const rowKeys = useMemo(() => rows.map((p) => p.uuid), [rows]);
  const drawerPerson = rows.find((p) => p.uuid === drawerUuid && p.kind === 'Personnel') ?? null;

  // Çekmecenin kişisi listeden kalktı (silindi / başka sekmede çıkarıldı) → çekmeceyi kapat, odak
  // "Personel Oluştur"e insin. Yalnız başarılı sorguda: tazeleme hatası çekmeceyi kapatmaz.
  useEffect(() => {
    if (drawerUuid !== null && drawerPerson === null && query.isSuccess) {
      openerRef.current = createOpenerRef.current;
      setDrawerUuid(null);
    }
  }, [drawerUuid, drawerPerson, query.isSuccess]);

  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  });
  const openFromNavigation = useCallback((uuid: string) => {
    const hit = rowsRef.current.find((p) => p.uuid.toLowerCase() === uuid.toLowerCase());
    if (!hit) {
      setNotFound(true);
      return;
    }
    setNotFound(false); // sonraki istek kişisini buldu → önceki "bulunamadı" bildirimi bayat
    if (hit.kind !== 'Personnel') {
      return; // davet: düz metin satır, açılacak çekmece yok
    }
    openerRef.current = nameButtons.current.get(hit.uuid) ?? null; // odak kişinin adına döner
    setDrawerUuid(hit.uuid);
  }, []);
  // Arka plan tazelemesi sürerken önbellekteki bayat listeye bakılmaz (yeni aktive kişi "bulunamadı" olmasın).
  useConsumeOpenPersonnel({
    ready: query.isSuccess && !query.isFetching,
    onOpen: openFromNavigation,
  });

  // Menü öğesinin kendisi menü kapanınca DOM'dan kalkar; dönüş hedefi satırın adı, yoksa ⋮'si.
  function rowOpener(person: ContractorPersonnelDto) {
    return (
      nameButtons.current.get(person.uuid) ??
      triggers.current.get(person.uuid) ??
      createOpenerRef.current
    );
  }
  const links = usePersonnelLinkActions({
    activation,
    rowKeys,
    stamps: { dataUpdatedAt: query.dataUpdatedAt, errorUpdatedAt: query.errorUpdatedAt },
    addButtonRef: createOpenerRef,
    dialogOpen: toDelete !== null,
    otherSurfaceOpen: createOpen || drawerPerson !== null,
    rowOpener,
    triggers,
    honorPersonalDataMask: true,
  });
  const { focus } = links;

  function invalidatePersonnel() {
    queryClient.invalidateQueries({ queryKey: contractorsKeys.personnel(contractorUuid) });
    // Silinen kişi izin adayı olmaktan hemen çıksın.
    queryClient.invalidateQueries({ queryKey: permitsKeys.personnelOptions(contractorUuid) });
  }

  const deleteMutation = useMutation({
    mutationFn: (userUuid: string) => contractorsApi.deletePersonnel(contractorUuid, userUuid),
    onSuccess: (_data, userUuid) => {
      invalidatePersonnel();
      // Personel bu taşerondan çıktı → diyaloğun kapanış dönüşü artık "Personel Oluştur"e iner.
      focus.removed(userUuid);
      setToDelete(null);
    },
    // 404 PERSONNEL_NOT_FOUND: bayat liste → tazele; satır yenilemede kalkarsa odak talebi kurulu.
    onError: (_error, userUuid) => {
      invalidatePersonnel();
      focus.removeFailed(userUuid);
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <TabPanelIntro
        action={
          <Button ref={createOpenerRef} size="sm" onClick={() => setCreateOpen(true)}>
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            {t('contractors.personnel.create')}
          </Button>
        }
      >
        {t('contractors.personnel.subtitle')}
      </TabPanelIntro>

      {notFound ? (
        // `Alert`in kapat düğmesi yalnız Carbon derlemesinde görünür → varsayılan temada kendi
        // ghost düğmemiz (`onDismiss` verilmez, çift düğme olmasın).
        <Alert tone="info">
          <div className="flex items-center justify-between gap-2">
            <span>{t('contractors.personnelDocs.notFound')}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setNotFound(false);
                createOpenerRef.current?.focus(); // düğme kalkınca odak <body>ye düşmesin
              }}
            >
              {t('common.close')}
            </Button>
          </div>
        </Alert>
      ) : null}

      {rows.some((r) => r.personalDataMasked) ? <PersonnelMaskNotice /> : null}
      {/* `relative`: sütun başlığındaki `sr-only` metnin kapsayıcı bloğu tablo olsun (Araçlar
          sekmesiyle aynı). İletişim sütunu sıkışmayı emer (≥sm `w-full` + `max-w-0`: içerik
          genişliği tabloya katkı vermez, kalan genişliği alır ve kırpılır); <sm tablo zaten
          kendi kabında kayar → sabit 12rem. */}
      <Table caption={t('contractors.personnel.title')} className="relative">
        <TableHeader>
          <TableRow>
            <TableHead>{t('contractors.personnel.col.name')}</TableHead>
            <TableHead
              className="w-full truncate sm:max-w-0"
              title={t('contractors.personnel.col.contact')}
            >
              {t('contractors.personnel.col.contact')}
            </TableHead>
            <TableHead>{t('contractors.personnel.col.status')}</TableHead>
            <TableHead>{t('contractors.personnel.col.access')}</TableHead>
            <TableHead>{t('contractors.personnel.col.date')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('contractors.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isPending ? (
            <TableMessageRow colSpan={COLUMN_COUNT}>
              <Spinner label={t('contractors.common.loading')} />
            </TableMessageRow>
          ) : query.isError ? (
            <TableMessageRow colSpan={COLUMN_COUNT}>
              {resolveApiErrorMessage(query.error, t)}
            </TableMessageRow>
          ) : rows.length === 0 ? (
            <TableMessageRow colSpan={COLUMN_COUNT}>
              <EmptyState icon={Users} title={t('contractors.personnel.empty')} />
            </TableMessageRow>
          ) : (
            rows.map((person) => {
              // Davet satırı yalnız adı gösterir (iletişim kendi sütununda); aktive personelin düğmesi,
              // `title`, ⋮ etiketi ve onaylar `personLabel`'ı (ad, yoksa iletişim) okur.
              const name = personLabel({ firstName: person.firstName, lastName: person.lastName });
              const label = personLabel(person);
              return (
                <TableRow key={person.uuid}>
                  {/* Satırın birincil "aç" eylemi: aktive personelde ad (adsızsa iletişim) ortak kişi
                      çekmecesini açan bir düğme (`PermitPersonnelPanel` / `VehiclesPage` ile aynı
                      sınıf); bekleyen davette (kind = Invitation) açılacak çekmece yok, ad düz metin
                      kalır. Uzun ad 8rem'de kırpılır, tamamı `title`da. Ne ad ne iletişim varsa
                      soluk "—" (boş bir odak hedefi olmasın). */}
                  <TableCell truncate={false}>
                    <div className="max-w-[8rem]">
                      {person.kind === 'Personnel' && label ? (
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) {
                              nameButtons.current.set(person.uuid, el);
                            } else {
                              nameButtons.current.delete(person.uuid);
                            }
                          }}
                          title={label}
                          onClick={(e) => {
                            openerRef.current = e.currentTarget;
                            setDrawerUuid(person.uuid);
                          }}
                          className="block w-full truncate text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {label}
                        </button>
                      ) : person.kind === 'Invitation' && name ? (
                        <span className="block truncate font-medium text-fg" title={label}>
                          {name}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell truncate={false} className="text-muted sm:max-w-0">
                    <span
                      className="block w-48 truncate sm:w-auto"
                      title={person.pendingLink?.emailOrPhone ?? person.emailOrPhone ?? undefined}
                    >
                      {person.pendingLink?.emailOrPhone ?? person.emailOrPhone ?? '—'}
                    </span>
                    {person.pendingLink ? <PendingLinkLine link={person.pendingLink} /> : null}
                  </TableCell>
                  <TableCell>
                    <Tag color={personnelStatusTone(person.status)}>
                      {t(personnelStatusLabelKey(person))}
                    </Tag>
                  </TableCell>
                  <TableCell>
                    <Tag color={accessLevelTone(person.accessLevel)}>
                      {t(`contractors.personnel.access.${person.accessLevel.toLowerCase()}`)}
                    </Tag>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted">
                    {formatDate(relevantDate(person), culture)}
                  </TableCell>
                  <TableCell className="w-12 px-2">
                    {/* İkincil/yıkıcı eylemler tek `⋮` menüsünde (owner review B1 item 4 — bu
                        panel eskiden HER ZAMAN görünen ikon düğmeleri kullanıyordu, diğer liste
                        sayfalarıyla tutarsızdı; ortak `RowActionsMenu` deseniyle hizalandı).
                        `isActive` notu KORUNDU: `null` görülürse Düzenle öğesi hiç eklenmez
                        (tahminle açık bırakılmaz), `toEditTarget` aynı koşulu bağımsızca tekrar
                        denetler (hedef yoksa çekmece açılmaz). */}
                    <RowActionsMenu
                      ariaLabel={t('contractors.personnel.action.openMenu', {
                        name: personLabel(person),
                      })}
                      triggerRef={(el) => {
                        if (el) {
                          triggers.current.set(person.uuid, el);
                        } else {
                          triggers.current.delete(person.uuid);
                        }
                      }}
                      items={
                        person.kind === 'Personnel'
                          ? [
                              ...(typeof person.isActive === 'boolean'
                                ? [
                                    {
                                      key: 'edit',
                                      label: t('personnelForm.edit.action'),
                                      // `begin` + açılış TEK olayda → ilk kare düzenleme kipi.
                                      onSelect: () => {
                                        openerRef.current = rowOpener(person);
                                        const target = toEditTarget(person);
                                        if (target) {
                                          editor.begin(target);
                                          setDrawerUuid(person.uuid);
                                        }
                                      },
                                    },
                                  ]
                                : []),
                              ...links.linkItems(person),
                              {
                                key: 'delete',
                                label: t('contractors.personnel.action.delete'),
                                destructive: true,
                                onSelect: () => {
                                  deleteMutation.reset();
                                  links.rememberRow(person);
                                  setToDelete(person);
                                },
                              },
                            ]
                          : links.linkItems(person)
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <PersonnelDrawer
        contractorUuid={contractorUuid}
        open={drawerPerson !== null}
        onOpenChange={(open) => !open && setDrawerUuid(null)}
        context={drawerPerson ? { kind: 'registry', person: drawerPerson } : null}
        restoreFocusRef={openerRef}
        editor={editor}
      />

      <PersonnelCreateSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        client={activation}
        restoreFocusRef={createOpenerRef}
      />

      <PersonnelLinkModal {...links.linkModalProps} />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title={t('contractors.personnel.delete.title')}
        body={t('contractors.personnel.delete.body', {
          name: toDelete ? personLabel(toDelete) : '',
        })}
        confirmLabel={t('contractors.personnel.delete.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />

      <ConfirmDialog {...links.cancelDialogProps} />
    </div>
  );
}
